import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Send } from 'lucide-react';
import {
  MAX_MESSAGE_LENGTH,
  STUDENT_MESSAGING_REFRESH_MS,
  isRefusal,
  mergeLatestPage,
  prependOlderPage,
  studentMessagingApi,
  studentMessagingError,
  studentMessagingKeys,
  type StudentMessage,
  type StudentThread,
} from '../api/studentMessaging';
import { useAuthStore } from '../store/auth';
import dayjs from '../lib/dates';
import {
  ErrorState,
  KIND_LABELS,
  Loading,
  OFFLINE,
  RefreshNotice,
  card,
  field,
  primaryButton,
  secondaryButton,
} from './StudentMessageParts';

/** Heure pour aujourd'hui, date et heure sinon. */
function messageStamp(iso: string): string {
  const date = dayjs(iso);
  const now = dayjs();
  if (date.isSame(now, 'day')) return date.format('HH:mm');
  return date.format(date.isSame(now, 'year') ? 'D MMM HH:mm' : 'D MMM YYYY HH:mm');
}

function Bubble({ message }: { message: StudentMessage }) {
  return (
    <li
      className={`flex flex-col ${message.mine ? 'items-end' : 'items-start'}`}
    >
      {!message.mine && (
        <span className="px-1 mb-1 text-xs text-slate-500">
          <span className="font-medium text-slate-700">{message.author}</span>
          {' · '}
          {messageStamp(message.createdAt)}
        </span>
      )}
      <p
        className={`max-w-[85%] sm:max-w-[70%] px-3.5 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
          message.mine
            ? 'bg-indigo-600 text-white rounded-br-md'
            : 'bg-slate-100 text-slate-900 rounded-bl-md'
        }`}
      >
        {message.body}
      </p>
      {message.mine && (
        <span className="relative px-1 mt-1 text-[11px] text-slate-400">
          <span className="sr-only">Vous · </span>
          {messageStamp(message.createdAt)}
        </span>
      )}
    </li>
  );
}

/** Fil d'un échange : messages, lecture signalée, rafraîchissement, saisie. */
export default function StudentMessageThread({
  conversationId,
  fallbackTitle,
  onBack,
}: {
  conversationId: string;
  fallbackTitle?: string;
  onBack: () => void;
}) {
  const userId = useAuthStore((s) => s.user?.id);
  const qc = useQueryClient();
  const threadKey = studentMessagingKeys.thread(userId, conversationId);
  const conversationsKey = studentMessagingKeys.conversations(userId);
  const [text, setText] = useState('');

  const thread = useQuery({
    queryKey: threadKey,
    queryFn: async () => {
      const latest = await studentMessagingApi.getMessages(conversationId);
      // Lu après la réponse : les messages précédents chargés entre-temps sont gardés.
      return mergeLatestPage(qc.getQueryData<StudentThread>(threadKey), latest);
    },
    refetchInterval: STUDENT_MESSAGING_REFRESH_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
    retry: false,
    gcTime: 0,
  });
  // Accès refusé ou rien à montrer : le contenu est masqué.
  const blocked = thread.isError && (!thread.data || isRefusal(thread.error));
  const data = blocked ? undefined : thread.data;
  const items = data?.items;
  const oldest = items?.length ? items[0].sequence : 0;
  const newest = items?.length ? items[items.length - 1].sequence : 0;
  const lastIsMine = items?.length ? items[items.length - 1].mine : false;

  const older = useMutation({
    mutationFn: (before: number) =>
      studentMessagingApi.getMessages(conversationId, before),
    onSuccess: (page) =>
      qc.setQueryData<StudentThread>(threadKey, (shown) =>
        shown ? prependOlderPage(shown, page) : shown,
      ),
  });

  // Lecture : le fil est ouvert, on signale le dernier message affiché.
  const marked = useRef(0);
  const refreshedAt = thread.dataUpdatedAt;
  useEffect(() => {
    if (!newest || newest <= marked.current) return;
    const previous = marked.current;
    marked.current = newest;
    studentMessagingApi.markRead(conversationId, newest).then(
      () =>
        void qc.invalidateQueries({
          queryKey: studentMessagingKeys.conversations(userId),
        }),
      () => {
        // Nouvel essai au prochain rafraîchissement du fil.
        if (marked.current === newest) marked.current = previous;
      },
    );
  }, [newest, refreshedAt, conversationId, userId, qc]);

  // Un nouvel essai du même texte reprend son identifiant : pas de doublon.
  const pending = useRef({ signature: '', requestId: '' });
  const send = useMutation({
    mutationFn: (body: string) => {
      if (pending.current.signature !== body)
        pending.current = { signature: body, requestId: crypto.randomUUID() };
      return studentMessagingApi.sendMessage(conversationId, {
        requestId: pending.current.requestId,
        body,
      });
    },
    onSuccess: (sent) => {
      pending.current = { signature: '', requestId: '' };
      // L'envoi vaut lecture jusqu'à ce message, côté serveur.
      marked.current = Math.max(marked.current, sent.sequence);
      setText('');
      void qc.invalidateQueries({ queryKey: threadKey });
      void qc.invalidateQueries({ queryKey: conversationsKey });
    },
  });

  function submit() {
    const body = text.trim();
    if (!body || send.isPending) return;
    send.mutate(body);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    submit();
  }

  // Défilement : en bas à l'ouverture et à l'arrivée d'un message si l'on y
  // était déjà ; sur place quand des messages précédents s'ajoutent en tête.
  const scroller = useRef<HTMLDivElement>(null);
  const shown = useRef({ newest: 0, oldest: 0 });
  /** Distance entre le haut de la zone visible et le bas du fil. */
  const fromBottom = useRef(0);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) {
      shown.current = { newest: 0, oldest: 0 };
      return;
    }
    const previous = shown.current;
    if (newest !== previous.newest) {
      const wasAtBottom = fromBottom.current - el.clientHeight < 80;
      if (!previous.newest || wasAtBottom || lastIsMine)
        el.scrollTop = el.scrollHeight;
    } else if (oldest !== previous.oldest) {
      el.scrollTop = el.scrollHeight - fromBottom.current;
    }
    shown.current = { newest, oldest };
    fromBottom.current = el.scrollHeight - el.scrollTop;
  }, [newest, oldest, lastIsMine]);

  const conversation = data?.conversation;
  const title = conversation?.title ?? fallbackTitle ?? 'Échange';
  const members = data?.members ?? [];

  return (
    <div
      className={`${card} flex flex-col overflow-hidden h-[calc(100dvh-11rem)] lg:h-[calc(100dvh-13rem)] min-h-[24rem]`}
    >
      <div className="flex items-center gap-2 px-3 py-3 border-b border-slate-100">
        <button
          type="button"
          onClick={onBack}
          aria-label="Retour aux échanges"
          className="lg:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0 px-1">
          <h2 className="text-base font-semibold text-slate-900 truncate">
            {title}
          </h2>
          {conversation && (
            <p className="text-xs text-slate-500 truncate">
              {KIND_LABELS[conversation.kind] ?? 'Échange'}
              {conversation.kind === 'GROUP' && members.length > 0
                ? ` · ${members.map((m) => m.name).join(', ')}`
                : ''}
            </p>
          )}
        </div>
      </div>

      {blocked ? (
        <ErrorState
          message={studentMessagingError(thread.error, OFFLINE)}
          onRetry={() => void thread.refetch()}
        />
      ) : !data ? (
        <Loading label="Chargement des messages…" />
      ) : (
        <>
          {thread.isError && (
            <RefreshNotice
              error={thread.error}
              onRetry={() => void thread.refetch()}
            />
          )}
          <div
            ref={scroller}
            role="log"
            aria-label="Fil des messages"
            onScroll={(e) => {
              const el = e.currentTarget;
              fromBottom.current = el.scrollHeight - el.scrollTop;
            }}
            className="relative flex-1 overflow-y-auto px-4 py-4"
          >
            {data.hasMore && (
              <div className="mb-4 text-center">
                <button
                  type="button"
                  className={secondaryButton}
                  disabled={older.isPending}
                  onClick={() => older.mutate(oldest)}
                >
                  {older.isPending && (
                    <Loader2 size={15} className="animate-spin" />
                  )}
                  Messages précédents
                </button>
                {older.isError && (
                  <p role="alert" className="text-sm text-red-600 mt-2">
                    {studentMessagingError(older.error, OFFLINE)}
                  </p>
                )}
              </div>
            )}
            {data.items.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-500">
                Aucun message pour le moment. Écrivez le premier.
              </p>
            ) : (
              <ol className="space-y-3">
                {data.items.map((message) => (
                  <Bubble key={message.id} message={message} />
                ))}
              </ol>
            )}
          </div>

          {data.conversation.canWrite ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
              className="border-t border-slate-100 p-3"
            >
              {send.isError && (
                <p role="alert" className="text-sm text-red-600 mb-2">
                  {studentMessagingError(send.error, OFFLINE)}
                </p>
              )}
              <div className="flex items-end gap-2">
                <label className="relative flex-1 min-w-0">
                  <span className="sr-only">Votre message</span>
                  <textarea
                    rows={2}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={onKeyDown}
                    // Figé pendant l'envoi, sans perdre le clavier du téléphone.
                    readOnly={send.isPending}
                    maxLength={MAX_MESSAGE_LENGTH}
                    placeholder="Écrire un message…"
                    className={`${field} block resize-none`}
                  />
                </label>
                <button
                  type="submit"
                  aria-label="Envoyer"
                  disabled={!text.trim() || send.isPending}
                  className={`${primaryButton} h-11 flex-shrink-0`}
                >
                  {send.isPending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                  <span className="hidden sm:inline">Envoyer</span>
                </button>
              </div>
              <p className="hidden sm:block mt-1.5 text-xs text-slate-400">
                Entrée pour envoyer · Maj+Entrée pour aller à la ligne
              </p>
            </form>
          ) : (
            <p className="border-t border-slate-100 p-4 text-sm text-slate-500">
              Compte en lecture seule : vous ne pouvez pas répondre.
            </p>
          )}
        </>
      )}
    </div>
  );
}
